/**
 * HandOfGloryCard - Shareable Cribbage Hand & Glory Card Generator
 * Creates luxury graphic cards for high-scoring hands (20+, 28, 29, 50+ crazy combos),
 * featuring HTML preview, HTML5 Canvas rendering (1200x675 social format),
 * PNG file download, direct clipboard image copy, Reddit sharing, and mobile Web Share API.
 */

import { TraditionalCard } from './TraditionalCard.js';

export class HandOfGloryCard {
  /**
   * Normalizes arbitrary brand strings to 'crazy', 'clash', 'pints', or 'adult'.
   * @param {string} brand
   * @returns {string}
   */
  static normalizeBrand(brand) {
    if (!brand) return 'crazy';
    const s = String(brand).toLowerCase().trim();
    if (s === 'clash' || s === 'cribbage-clash' || s.includes('clash')) return 'clash';
    if (s === 'pints' || s === 'pints-and-pegs' || s === 'cribbage-pints' || s.includes('pints')) return 'pints';
    if (s === 'adult' || s === 'stripped' || s === 'skunked-and-stripped' || s.includes('stripped') || s.includes('adult') || s.includes('skunked')) return 'adult';
    return 'crazy';
  }

  /**
   * Resolves full branding metadata (titles, canonical URLs, display URLs, slugs) for a game brand.
   * @param {string} [brandKey='crazy']
   * @returns {{ id: string, name: string, brandTitle: string, slug: string, path: string, url: string, displayUrl: string }}
   */
  static getBrandInfo(brandKey = 'crazy') {
    const brand = HandOfGloryCard.normalizeBrand(brandKey);
    const map = {
      crazy: {
        id: 'crazy',
        name: 'Crazy Cribbage',
        brandTitle: 'CRAZY CRIBBAGE',
        slug: 'game-crazy-cribbage',
        path: '/pages/game-crazy-cribbage',
        url: 'https://customcribboards.com/pages/game-crazy-cribbage',
        displayUrl: 'customcribboards.com/pages/game-crazy-cribbage'
      },
      clash: {
        id: 'clash',
        name: 'Cribbage Clash',
        brandTitle: 'CRIBBAGE CLASH',
        slug: 'game-cribbage-clash',
        path: '/pages/game-cribbage-clash',
        url: 'https://customcribboards.com/pages/game-cribbage-clash',
        displayUrl: 'customcribboards.com/pages/game-cribbage-clash'
      },
      pints: {
        id: 'pints',
        name: 'Pints & Pegs',
        brandTitle: 'PINTS AND PEGS',
        slug: 'game-pints-and-pegs',
        path: '/pages/game-pints-and-pegs',
        url: 'https://customcribboards.com/pages/game-pints-and-pegs',
        displayUrl: 'customcribboards.com/pages/game-pints-and-pegs'
      },
      adult: {
        id: 'adult',
        name: 'Skunked & Stripped',
        brandTitle: 'SKUNKED AND STRIPPED',
        slug: 'game-skunked-and-stripped',
        path: '/pages/game-skunked-and-stripped',
        url: 'https://customcribboards.com/pages/game-skunked-and-stripped',
        displayUrl: 'customcribboards.com/pages/game-skunked-and-stripped'
      }
    };
    return map[brand] || map.crazy;
  }

  /**
   * Detects the active game brand from context, settings, global state, DOM attributes, or URL.
   * @param {Object} [context={}]
   * @returns {string}
   */
  static detectBrand(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const state = context.state || (typeof window !== 'undefined' ? window.state : null) || {};

    // 1. Explicit brand in context, data, or state
    if (context.brand) return HandOfGloryCard.normalizeBrand(context.brand);
    if (context.currentBrand) return HandOfGloryCard.normalizeBrand(context.currentBrand);
    if (context.hand && context.hand.brand) return HandOfGloryCard.normalizeBrand(context.hand.brand);
    if (settings.brand) return HandOfGloryCard.normalizeBrand(settings.brand);
    if (state.brand) return HandOfGloryCard.normalizeBrand(state.brand);

    // 2. Global window brand properties
    if (typeof window !== 'undefined') {
      if (window.currentBrand) return HandOfGloryCard.normalizeBrand(window.currentBrand);
      if (window.currentLogoBrand) return HandOfGloryCard.normalizeBrand(window.currentLogoBrand);
      if (typeof window.detectBrand === 'function') {
        try {
          const b = window.detectBrand();
          if (b) return HandOfGloryCard.normalizeBrand(b);
        } catch (e) {}
      }
    }

    // 3. Document element data-brand attribute
    if (typeof document !== 'undefined' && document.documentElement) {
      const b = document.documentElement.getAttribute('data-brand');
      if (b) return HandOfGloryCard.normalizeBrand(b);
    }

    // 4. URL query parameters and pathname
    if (typeof window !== 'undefined' && window.location) {
      try {
        const params = new URLSearchParams(window.location.search);
        const p = (params.get('brand') || params.get('game') || params.get('logo') || '').toLowerCase();
        if (p) return HandOfGloryCard.normalizeBrand(p);

        const href = (window.location.href || '').toLowerCase();
        if (href.includes('game-cribbage-clash') || href.includes('cribbage-clash') || href.includes('clash')) return 'clash';
        if (href.includes('game-pints-and-pegs') || href.includes('pints-and-pegs') || href.includes('pints')) return 'pints';
        if (href.includes('game-skunked-and-stripped') || href.includes('skunked-and-stripped') || href.includes('adult') || href.includes('stripped')) return 'adult';
        if (href.includes('game-crazy-cribbage') || href.includes('crazy-cribbage') || href.includes('crazy')) return 'crazy';
      } catch (e) {}
    }

    return 'crazy';
  }

  /**
   * Normalizes hand data from state, lastShow, or raw object.
   * @param {string|Object} keyOrData
   * @param {Object} [context={}]
   * @returns {Object|null}
   */
  static extractHandData(keyOrData, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null) || {};
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const pName = context.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (w => w === 'you' ? 'You' : w === 'cpu' ? 'Computer' : w));

    let h = null;

    if (typeof keyOrData === 'string') {
      const ls = state.lastShow || {};
      h = ls[keyOrData] || null;
      if (!h) {
        // Fallback: match by who, seat, or id
        for (const k of Object.keys(ls)) {
          if (ls[k] && (ls[k].who === keyOrData || ls[k].seat === keyOrData || ls[k].id === keyOrData)) {
            h = ls[k];
            break;
          }
        }
      }
      if (keyOrData === 'best') {
        if (state.bestMatchHand) {
          h = state.bestMatchHand;
        } else if (typeof window !== 'undefined' && window.state && window.state.bestMatchHand) {
          h = window.state.bestMatchHand;
        }
      }
    } else if (keyOrData && typeof keyOrData === 'object') {
      h = keyOrData;
    }

    if (!h) {
      // Fallback: check if there's any lastShow entry available
      const ls = state.lastShow || {};
      h = ls.pone || ls.dealer || ls.crib || ls.p1 || ls.p2 || (Object.values(ls)[0] || null);
    }

    if (!h) return null;

    const isCrib = !!(h.isCrib || (typeof keyOrData === 'string' && keyOrData === 'crib'));

    const cards = (h.cards || []).map(c => ({
      r: c.r || c.rank || '',
      s: c.s || c.suit || '',
      joker: !!(c.joker || c.isJoker)
    }));

    const startersList = (h.starters && h.starters.length) ? h.starters : (state.starters && state.starters.length ? state.starters : []);
    const starter = h.starter ? {
      r: h.starter.r || h.starter.rank || '',
      s: h.starter.s || h.starter.suit || '',
      joker: !!(h.starter.joker || h.starter.isJoker)
    } : (startersList.length ? {
      r: startersList[0].r || startersList[0].rank || '',
      s: startersList[0].s || startersList[0].suit || '',
      joker: !!(startersList[0].joker || startersList[0].isJoker)
    } : (state.starter ? {
      r: state.starter.r || state.starter.rank || '',
      s: state.starter.s || state.starter.suit || '',
      joker: !!(state.starter.joker || state.starter.isJoker)
    } : null));

    const scoreTotal = typeof h.signed === 'number'
      ? h.signed
      : (h.score && typeof h.score.total === 'number' ? h.score.total : (h.points || 0));

    let lines = (h.score && h.score.lines) || h.lines || [];
    let notes = (h.score && h.score.notes) || h.notes || [];

    // Fallback: If lines array is empty but we have cards, attempt re-scoring with CribbageScorer
    if ((!lines || lines.length === 0) && cards.length > 0) {
      try {
        if (typeof CribbageScorer !== 'undefined' && CribbageScorer.scoreHand) {
          const reScored = CribbageScorer.scoreHand(cards, starter, isCrib, settings);
          if (reScored && reScored.lines && reScored.lines.length) {
            lines = reScored.lines;
            notes = reScored.notes || notes;
          }
        }
      } catch (e) {}
    }

    const ownerName = h.title || pName(h.who || 'you');

    // Detect game brand & brand metadata
    const brand = HandOfGloryCard.detectBrand({ ...context, keyOrData, hand: h, settings, state });
    const brandInfo = HandOfGloryCard.getBrandInfo(brand);

    let titleTag = 'HAND OF GLORY';
    if (scoreTotal === 29) titleTag = 'THE HOLY GRAIL';
    else if (scoreTotal === 28) titleTag = 'TWENTY-EIGHT';
    else if (scoreTotal >= 50) titleTag = 'CRAZY CENTURY';
    else if (scoreTotal >= 30) titleTag = 'MONSTER HAND';
    else if (scoreTotal < 20) titleTag = 'HAND BREAKDOWN';

    return {
      brand: brandInfo.id,
      brandName: brandInfo.name,
      brandTitle: brandInfo.brandTitle,
      brandUrl: brandInfo.url,
      brandDisplayUrl: brandInfo.displayUrl,
      brandSlug: brandInfo.slug,
      score: scoreTotal,
      titleTag,
      ownerName,
      isCrib,
      cards,
      starter,
      lines,
      notes,
      date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    };
  }

  /**
   * Checks whether a scoring line represents a Crazy Cribbage bonus or specialty rule.
   * @param {Object} l
   * @returns {boolean}
   */
  static isBonusLine(l) {
    if (!l) return false;
    if (l.isBonus) return true;
    const w = String(l.why || '').toLowerCase();
    return /all diff|diff.*suit|different suit|all red|all black|all odd|all even|flush in play|suicide|queen of spades|ace of spades|full house|straight flush|straight|royal flush|nineteen|thirty-one|31 for 5|19 for 2|bonus|zero hand|zero crib|rock crib/i.test(w);
  }

  /**
   * Helper to format a card label string (e.g. 5♥, J♦, Joker).
   * @param {Object} c
   * @returns {string}
   */
  static formatCardText(c) {
    if (!c) return '';
    if (c.joker) return '★ Joker';
    const r = c.r || '';
    const rankNames = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
    const rName = rankNames[r] || r;
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    return `${rName}${suitSyms[c.s] || c.s || ''}`;
  }

  /**
   * Builds the HTML preview for the Hand of Glory card inside the modal.
   * @param {Object} data
   * @returns {string}
   */
  static buildCardHtml(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const displayUrl = data?.brandDisplayUrl || brandInfo.displayUrl;
    const brandTitle = data?.brandTitle || brandInfo.brandTitle;

    const isRedSuit = (s) => {
      const str = String(s || '').toUpperCase();
      return str === 'H' || str === 'D' || str.startsWith('HEART') || str.startsWith('DIAMOND');
    };
    const getSuitSym = (s) => {
      const str = String(s || '').toUpperCase();
      if (str === 'H' || str.startsWith('HEART')) return '♥';
      if (str === 'D' || str.startsWith('DIAMOND')) return '♦';
      if (str === 'S' || str.startsWith('SPADE')) return '♠';
      if (str === 'C' || str.startsWith('CLUB')) return '♣';
      return str;
    };
    const rankNames = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };

    const renderCard = (c, isCut = false) => {
      if (!c) return '';
      const red = isRedSuit(c.s);
      const colorClass = red ? 'card-red' : 'card-black';

      return `
        <div class="glory-card-box ${colorClass} ${isCut ? 'glory-cut-card' : ''}" style="border: 2px solid ${isCut ? '#f59e0b' : 'rgba(0,0,0,0.18)'}; background: #ffffff !important; position: relative;">
          ${TraditionalCard.renderHtml(c, { isCut })}
        </div>
      `;
    };

    const cardsHtml = data.cards.map(c => renderCard(c, false)).join('');
    const starterHtml = data.starter ? renderCard(data.starter, true) : '';

    const linesHtml = (data.lines || []).map(l => {
      const isBonus = HandOfGloryCard.isBonusLine(l);
      const sign = l.n > 0 ? `+${l.n}` : `${l.n}`;
      const bonusClass = isBonus ? ' glory-pill-bonus' : '';
      const bonusBadge = isBonus ? '<span class="glory-bonus-badge">★ BONUS</span> ' : '';
      return `<span class="glory-pill${bonusClass}"><strong>${sign}</strong> ${bonusBadge}${l.why}</span>`;
    }).join('');

    return `
      <div class="glory-card-outer" id="gloryCardOuter">
        <div class="glory-card-inner">
          <div class="glory-card-header">
            <div class="glory-brand-badge">${brandTitle}</div>
            <div class="glory-hand-title">${data.ownerName} · ${data.isCrib ? 'Crib' : 'Hand'}</div>
          </div>

          <div class="glory-score-wrap">
            <div class="glory-score-value">${data.score}</div>
            <div class="glory-score-label">POINTS</div>
            <div class="glory-tag-badge">${data.titleTag}</div>
          </div>

          <div class="glory-cards-row">
            ${cardsHtml}
            ${starterHtml ? `<div class="glory-starter-divider"></div>${starterHtml}` : ''}
          </div>

          ${linesHtml ? `<div class="glory-pills-row">${linesHtml}</div>` : ''}

          <div class="glory-card-footer">
            <span class="glory-url">${displayUrl}</span>
            <span class="glory-date">${data.date}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders the hand of glory card onto a crisp 1200x675 HTML5 Canvas for image generation.
   * @param {Object} data
   * @returns {HTMLCanvasElement}
   */
  static renderToCanvas(data) {
    const W = 1200;
    const H = 675;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // 1. Rich dark wood / felt tournament gradient background
    const bgGrad = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 700);
    bgGrad.addColorStop(0, '#1c150c');
    bgGrad.addColorStop(0.55, '#120d07');
    bgGrad.addColorStop(1, '#080503');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Subtle woodwork grain streaks
    ctx.save();
    ctx.globalAlpha = 0.035;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    for (let y = 0; y < H; y += 7) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(W * 0.35, y + 4, W * 0.65, y - 4, W, y);
      ctx.stroke();
    }
    ctx.restore();

    // 2. Ornate Metallic Gold Border
    ctx.save();
    ctx.strokeStyle = '#f0c64a';
    ctx.lineWidth = 4;
    ctx.strokeRect(28, 28, W - 56, H - 56);
    ctx.strokeStyle = 'rgba(240, 198, 74, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(36, 36, W - 72, H - 72);

    // Corner decorative diamonds
    const drawDiamond = (cx, cy, s) => {
      ctx.fillStyle = '#f0c64a';
      ctx.beginPath();
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy);
      ctx.lineTo(cx, cy + s);
      ctx.lineTo(cx - s, cy);
      ctx.closePath();
      ctx.fill();
    };
    drawDiamond(36, 36, 6);
    drawDiamond(W - 36, 36, 6);
    drawDiamond(36, H - 36, 6);
    drawDiamond(W - 36, H - 36, 6);
    ctx.restore();

    // 3. Top Header: Brand & Title
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = '800 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#f0c64a';
    ctx.fillText(data.brandTitle.toUpperCase() + ' · HAND OF GLORY', W / 2, 54);

    ctx.font = '600 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#d6d3d1';
    ctx.fillText(`${data.ownerName} · ${data.isCrib ? 'Crib' : 'Hand'}`, W / 2, 88);
    ctx.restore();

    // 4. Big Score Callout
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '900 86px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#f0c64a';
    ctx.shadowColor = 'rgba(240, 198, 74, 0.6)';
    ctx.shadowBlur = 18;
    ctx.fillText(String(data.score), W / 2, 168);
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    ctx.font = '800 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('POINTS', W / 2, 222);

    // Tag badge (e.g. THE HOLY GRAIL or HAND OF GLORY)
    const badgeText = `★  ${data.titleTag}  ★`;
    ctx.font = '700 15px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const tagW = ctx.measureText(badgeText).width + 36;
    ctx.fillStyle = 'rgba(240, 198, 74, 0.15)';
    ctx.strokeStyle = '#f0c64a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect((W - tagW) / 2, 240, tagW, 28, 14);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#fef08a';
    ctx.fillText(badgeText, W / 2, 254);
    ctx.restore();

    // 5. Playing Cards Display
    const allCards = [...data.cards];
    if (data.starter) allCards.push({ ...data.starter, isStarter: true });

    const totalCards = allCards.length;
    const cardW = 108;
    const cardH = 152;
    const gap = 16;
    const dividerGap = data.starter ? 28 : 0;
    const rowW = (totalCards * cardW) + ((totalCards - 1) * gap) + dividerGap;
    let startX = (W - rowW) / 2;
    const cardY = 300;

    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    const rankNames = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };

    allCards.forEach((c) => {
      if (c.isStarter) startX += dividerGap;

      ctx.save();
      // Card Shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
      ctx.shadowBlur = 14;
      ctx.shadowOffsetY = 6;

      // Card Body
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(startX, cardY, cardW, cardH, 10);
      ctx.fill();

      // Clear all shadow parameters immediately so card border, badges, numbers, and suits have zero drop shadow
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Border
      if (c.isStarter) {
        ctx.strokeStyle = '#f59e0b';
        ctx.lineWidth = 3.5;
      } else if (c.joker) {
        ctx.strokeStyle = '#9333ea';
        ctx.lineWidth = 2.5;
      } else {
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 1.5;
      }
      ctx.stroke();

      // Starter badge
      if (c.isStarter) {
        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.roundRect(startX + 18, cardY - 11, cardW - 36, 20, 10);
        ctx.fill();
        ctx.fillStyle = '#1e1b18';
        ctx.font = '900 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('CUT', startX + (cardW / 2), cardY - 1);
      }

      // Ensure zero drop shadow on card numbers, ranks, and suit glyphs
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Draw traditional playing card (2 has 2 suits, 3 has 3 suits, etc.)
      TraditionalCard.drawToCanvas(ctx, c, startX, cardY, cardW, cardH, { isStarter: c.isStarter, isCut: c.isStarter, joker: c.joker });

      ctx.restore();
      startX += cardW + gap;
    });

    // 6. Combination Breakdown summary chips (dynamically wraps into multiple centered rows)
    const allLines = data.lines || [];
    if (allLines.length) {
      ctx.save();
      ctx.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textBaseline = 'middle';

      // Measure all chips
      const chips = allLines.map(l => {
        const isBonus = HandOfGloryCard.isBonusLine(l);
        const prefix = l.n > 0 ? `+${l.n}` : `${l.n}`;
        const label = isBonus ? `★ ${prefix} ${l.why}` : `${prefix} ${l.why}`;
        const textW = ctx.measureText(label).width;
        const boxW = textW + 22;
        return { label, boxW, isBonus };
      });

      // Wrap chips into rows (max row width 1080px)
      const maxRowW = 1080;
      const rows = [];
      let currentRow = [];
      let currentRowW = 0;

      chips.forEach(chip => {
        const addedW = currentRow.length === 0 ? chip.boxW : (8 + chip.boxW);
        if (currentRow.length > 0 && (currentRowW + addedW > maxRowW)) {
          rows.push({ items: currentRow, totalW: currentRowW });
          currentRow = [chip];
          currentRowW = chip.boxW;
        } else {
          currentRow.push(chip);
          currentRowW += addedW;
        }
      });
      if (currentRow.length) {
        rows.push({ items: currentRow, totalW: currentRowW });
      }

      // Calculate vertical positioning centered between cards and footer
      const chipH = rows.length > 2 ? 24 : 26;
      const rowGap = 7;
      const totalBlockH = rows.length * chipH + (rows.length - 1) * rowGap;
      const blockStartY = 464 + Math.max(0, (126 - totalBlockH) / 2);

      rows.forEach((row, rIdx) => {
        let cx = (W - row.totalW) / 2;
        const cy = blockStartY + (rIdx * (chipH + rowGap)) + (chipH / 2);

        row.items.forEach(chip => {
          if (chip.isBonus) {
            ctx.fillStyle = 'rgba(245, 158, 11, 0.22)';
            ctx.strokeStyle = '#f59e0b';
            ctx.lineWidth = 1.4;
          } else {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
            ctx.strokeStyle = 'rgba(240, 198, 74, 0.38)';
            ctx.lineWidth = 1;
          }

          ctx.beginPath();
          ctx.roundRect(cx, cy - (chipH / 2), chip.boxW, chipH, 5);
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = chip.isBonus ? '#fef08a' : '#f4ead4';
          ctx.textAlign = 'left';
          ctx.fillText(chip.label, cx + 11, cy);

          cx += chip.boxW + 8;
        });
      });

      ctx.restore();
    }

    // 7. Footer Branding & Link
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandTitle = data?.brandTitle || brandInfo.brandTitle;

    ctx.save();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.font = '700 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#f0c64a';
    ctx.fillText(`${brandTitle} · CustomCribBoards.com`, 52, H - 48);

    ctx.font = '500 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = 'rgba(214, 211, 209, 0.7)';
    ctx.fillText('Play 100% Free Online: Solo vs AI, Pass & Play, or Online Room Codes', 52, H - 28);

    ctx.textAlign = 'right';
    ctx.fillText(data.date, W - 52, H - 38);
    ctx.restore();

    return canvas;
  }

  /**
   * Generates a PNG Blob from the rendered canvas.
   * @param {Object} data
   * @returns {Promise<Blob>}
   */
  static async getCardBlob(data) {
    const canvas = HandOfGloryCard.renderToCanvas(data);
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/png');
    });
  }

  /**
   * Triggers a download of the card PNG image.
   * @param {Object} data
   */
  static async downloadCardImage(data) {
    try {
      const blob = await HandOfGloryCard.getCardBlob(data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
      const fileSlug = brandInfo.slug || 'game-crazy-cribbage';
      const safeName = (data.titleTag || 'hand-of-glory').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.download = `${fileSlug}-${safeName}-${data.score}pts.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      HandOfGloryCard.showToast('📥 Hand of Glory card downloaded!');
    } catch (err) {
      console.error('Download error:', err);
      HandOfGloryCard.showToast('Failed to download image.');
    }
  }

  /**
   * Copies the card image directly to system clipboard as image/png.
   * @param {Object} data
   */
  static async copyCardImage(data) {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard || !navigator.clipboard.write) {
        throw new Error('Clipboard API not supported');
      }
      const blob = await HandOfGloryCard.getCardBlob(data);
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      HandOfGloryCard.showToast('📋 Image copied to clipboard!');
    } catch (err) {
      console.warn('Clipboard image write failed, copying text breakdown instead:', err);
      HandOfGloryCard.copyBreakdownText(data);
    }
  }

  /**
   * Copies formatted text breakdown of the hand to clipboard.
   * @param {Object} data
   */
  static async copyBreakdownText(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandName = data?.brandName || brandInfo.name;
    const shareUrl = data?.brandUrl || brandInfo.url;

    const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
    const cutStr = data.starter ? ` | Cut: ${HandOfGloryCard.formatCardText(data.starter)}` : '';
    const linesStr = (data.lines || []).map(l => {
      const isBonus = HandOfGloryCard.isBonusLine(l);
      const sign = l.n > 0 ? `+${l.n}` : `${l.n}`;
      return `• ${sign} ${l.why}${isBonus ? ' (★ Bonus)' : ''}`;
    }).join('\n');
    const text = `🏆 ${brandName} Hand of Glory: ${data.score} Points! (${data.titleTag})\n` +
      `Cards: [${cardStrs}${cutStr}]\n` +
      (linesStr ? `${linesStr}\n` : '') +
      `Play free: ${shareUrl}`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        HandOfGloryCard.showToast('📋 Hand summary copied to clipboard!');
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Native Mobile Web Share API integration (files + text).
   * @param {Object} data
   */
  static async shareNative(data) {
    try {
      if (typeof navigator === 'undefined' || !navigator.share) {
        HandOfGloryCard.downloadCardImage(data);
        return;
      }

      const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
      const brandName = data?.brandName || brandInfo.name;
      const shareUrl = data?.brandUrl || brandInfo.url;
      const fileSlug = brandInfo.slug || 'game-crazy-cribbage';

      const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
      const cutStr = data.starter ? ` + ${HandOfGloryCard.formatCardText(data.starter)} cut` : '';
      const text = `I just scored ${data.score} points (${data.titleTag}) in ${brandName}! [${cardStrs}${cutStr}]`;
      const url = shareUrl;

      try {
        const blob = await HandOfGloryCard.getCardBlob(data);
        const file = new File([blob], `${fileSlug}-${data.score}pts.png`, { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${brandName}: ${data.score} pt Hand!`,
            text,
            url,
            files: [file]
          });
          return;
        }
      } catch (fileErr) {
        console.warn('File sharing failed, attempting text share:', fileErr);
      }

      await navigator.share({
        title: `${brandName}: ${data.score} pt Hand!`,
        text,
        url
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Native share failed:', err);
      }
    }
  }

  /**
   * Opens Reddit submission window targeting r/cribbage.
   * @param {Object} data
   */
  static shareToReddit(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandName = data?.brandName || brandInfo.name;
    const shareUrl = data?.brandUrl || brandInfo.url;

    const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
    const cutStr = data.starter ? ` + ${HandOfGloryCard.formatCardText(data.starter)} cut` : '';
    const title = `Scored ${data.score} points in ${brandName}! [${cardStrs}${cutStr}]`;
    const url = `https://www.reddit.com/r/cribbage/submit?title=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Opens X / Twitter intent.
   * @param {Object} data
   */
  static shareToTwitter(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandName = data?.brandName || brandInfo.name;
    const shareUrl = data?.brandUrl || brandInfo.url;

    const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
    const cutStr = data.starter ? ` + ${HandOfGloryCard.formatCardText(data.starter)} cut` : '';
    const text = `Scored ${data.score} points in @CustomCribBoard ${brandName}! [${cardStrs}${cutStr}] 🃏🔥`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Opens Facebook share dialog.
   * @param {Object} data
   */
  static shareToFacebook(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandName = data?.brandName || brandInfo.name;
    const shareUrl = data?.brandUrl || brandInfo.url;

    const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
    const cutStr = data.starter ? ` + ${HandOfGloryCard.formatCardText(data.starter)} cut` : '';
    const quote = `Scored ${data.score} points in ${brandName}! [${cardStrs}${cutStr}]`;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(quote)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Opens Instagram directly and copies the card graphic to the clipboard for instant pasting.
   * @param {Object} data
   */
  static shareToInstagram(data) {
    // 1. Open Instagram directly
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');

    // 2. Copy card graphic to clipboard for pasting into Instagram Story / DM / Post
    HandOfGloryCard.copyCardImage(data);
  }

  /**
   * Opens Threads submission intent with prefilled hand breakdown and link.
   * @param {Object} data
   */
  static shareToThreads(data) {
    const brandInfo = HandOfGloryCard.getBrandInfo(data?.brand);
    const brandName = data?.brandName || brandInfo.name;
    const shareUrl = data?.brandUrl || brandInfo.url;

    const cardStrs = (data.cards || []).map(HandOfGloryCard.formatCardText).join(' ');
    const cutStr = data.starter ? ` + ${HandOfGloryCard.formatCardText(data.starter)} cut` : '';
    const text = `Scored ${data.score} points in @CustomCribBoard ${brandName}! [${cardStrs}${cutStr}] 🃏🔥\n\n${shareUrl}`;
    const url = `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Brief floating toast notification.
   * @param {string} msg
   */
  static showToast(msg) {
    if (typeof document === 'undefined') return;
    let toast = document.getElementById('gloryToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'gloryToast';
      toast.className = 'glory-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('visible');
    setTimeout(() => {
      if (toast) toast.classList.remove('visible');
    }, 2400);
  }

  /**
   * Displays the complete Hand of Glory Modal.
   * @param {string|Object} keyOrData
   * @param {Object} [context={}]
   */
  static showModal(keyOrData, context = {}) {
    if (typeof document === 'undefined') return;
    const data = HandOfGloryCard.extractHandData(keyOrData, context);
    if (!data) return;

    window._activeGloryCardData = data;

    const cardPreviewHtml = HandOfGloryCard.buildCardHtml(data);
    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    const modalHtml = `
      <div class="glory-modal-container">
        <div class="glory-modal-top">
          <div class="glory-modal-headline">🏆 HAND OF GLORY</div>
          <button type="button" class="glory-btn-close" data-action="close" aria-label="Close" title="Close">✕</button>
        </div>

        ${cardPreviewHtml}

        <div class="glory-action-bar">
          <button type="button" class="btn-glory-action btn-glory-download" data-action="download" title="Download high-resolution image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span>Download</span>
          </button>
          <button type="button" class="btn-glory-action btn-glory-copy" data-action="copy" title="Copy card graphic to clipboard">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            <span>Copy Image</span>
          </button>
          ${canNativeShare ? `
          <button type="button" class="btn-glory-action btn-glory-native" data-action="native" title="Share with phone apps">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            <span>Share</span>
          </button>` : ''}
          <button type="button" class="btn-glory-action btn-glory-reddit" data-action="reddit" title="Share to r/cribbage on Reddit">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/><path fill="#fff" d="M12 4.5c.8 0 1.5.7 1.5 1.5 0 .2-.05.4-.13.58l2.06 1.03c.53-.4 1.2-.61 1.92-.61 1.74 0 3.15 1.41 3.15 3.15 0 .73-.24 1.4-.66 1.94.05.27.08.55.08.83 0 2.87-3.58 5.2-8 5.2s-8-2.33-8-5.2c0-.28.03-.56.08-.83-.42-.54-.66-1.21-.66-1.94 0-1.74 1.41-3.15 3.15-3.15.72 0 1.39.21 1.92.61l2.06-1.03c-.08-.18-.13-.38-.13-.58 0-.8.7-1.5 1.5-1.5zm-3 8a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-5.5 2.5c.67.67 1.5 1 2.5 1s1.83-.33 2.5-1"/></svg>
            <span>Reddit</span>
          </button>
          <button type="button" class="btn-glory-action btn-glory-x" data-action="x" title="Share to X">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            <span>X</span>
          </button>
          <button type="button" class="btn-glory-action btn-glory-facebook" data-action="facebook" title="Share to Facebook">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <span>Facebook</span>
          </button>
          <button type="button" class="btn-glory-action btn-glory-instagram" data-action="instagram" title="Share to Instagram">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            <span>Instagram</span>
          </button>
          <button type="button" class="btn-glory-action btn-glory-threads" data-action="threads" title="Share to Threads">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.26 18.3 1.5 15.394 1.733 11.838c.418-6.384 4.887-11.393 10.63-11.82 5.093-.38 9.537 2.378 10.748 6.66.42 1.487.483 3.018.188 4.549a9.718 9.718 0 0 1-2.924 5.378 9.554 9.554 0 0 1-5.753 2.658c-1.326.175-2.618-.088-3.738-.76-.713-.427-1.305-1.01-1.748-1.713-.672.937-1.554 1.637-2.607 2.062a7.126 7.126 0 0 1-4.004.288c.324.636.745 1.209 1.259 1.706 1.488 1.439 3.518 2.213 6.033 2.302.267.01.536.015.805.015 2.535 0 5.074-.537 7.234-1.639.673-.343 1.49-.074 1.833.599.344.673.075 1.49-.598 1.833-2.502 1.277-5.441 1.9-8.384 1.9-.304 0-.608-.006-.911-.017zm2.463-7.797c.504-.08 1.004-.25 1.486-.508a6.992 6.992 0 0 0 2.457-2.42 6.842 6.842 0 0 0 .97-3.66c.162-.84.127-1.68-.104-2.497-.783-2.772-3.766-4.606-7.237-4.348-4.223.313-7.513 4.095-7.828 8.989-.174 2.698.396 4.887 1.696 6.505 1.349 1.68 3.398 2.545 6.091 2.57 2.052-.072 3.659-.724 4.793-1.841-.301-.482-.507-1.026-.6-1.603a4.57 4.57 0 0 1-.225-1.378c-.012-.435.032-.871.13-1.298a4.136 4.136 0 0 1 1.077-1.921 4.254 4.254 0 0 1 2.023-1.071c1.077-.247 2.146-.071 2.97.487-.315-1.815-.989-2.937-2.22-3.702-.997-.62-2.316-.957-3.818-.981a8.423 8.423 0 0 0-4.707 1.424 8.784 8.784 0 0 0-3.088 3.738 9.537 9.537 0 0 0-.649 4.316c.381 2.894 2.138 4.755 4.819 5.097 1.258.161 2.483-.075 3.541-.682.49-.281 1.115-.116 1.396.374.281.491.116 1.116-.374 1.397-1.42.815-3.064 1.132-4.754.915-3.67-.47-6.098-3.082-6.602-6.915a11.91 11.91 0 0 1 .812-5.394 11.002 11.002 0 0 1 3.864-4.678 10.685 10.685 0 0 1 5.952-1.794c1.94.032 3.684.475 5.044 1.323 1.959 1.218 3.012 3.09 3.125 5.767a1.144 1.144 0 0 1-.03.265c-.173 1.025-.568 2.012-1.173 2.936a7.272 7.272 0 0 1-2.072 2.046c-.958.583-2.036.883-3.116.868z"/></svg>
            <span>Threads</span>
          </button>
        </div>

        <div class="glory-modal-bottom-row">
          <button type="button" class="btn-ghost glory-btn-return" data-action="close">
            ← Return to Game
          </button>
        </div>
      </div>
    `;

    let overlay = document.getElementById('gloryOverlay');
    let box = document.getElementById('gloryModalBox');

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'gloryOverlay';
      overlay.className = 'glory-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Hand of Glory Card');
      box = document.createElement('div');
      box.id = 'gloryModalBox';
      box.className = 'glory-modal-box';
      overlay.appendChild(box);
      document.body.appendChild(overlay);
    }

    if (!overlay._hasGloryClickListener && typeof overlay.addEventListener === 'function') {
      overlay._hasGloryClickListener = true;
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          HandOfGloryCard.closeModal();
          return;
        }
        const actionBtn = (e.target && typeof e.target.closest === 'function') ? e.target.closest('[data-action]') : null;
        if (!actionBtn) return;
        const action = actionBtn.dataset ? actionBtn.dataset.action : actionBtn.getAttribute('data-action');
        switch (action) {
          case 'close':
            HandOfGloryCard.closeModal();
            break;
          case 'download':
            if (window._activeGloryCardData) HandOfGloryCard.downloadCardImage(window._activeGloryCardData);
            break;
          case 'copy':
            if (window._activeGloryCardData) HandOfGloryCard.copyCardImage(window._activeGloryCardData);
            break;
          case 'native':
            if (window._activeGloryCardData) HandOfGloryCard.shareNative(window._activeGloryCardData);
            break;
          case 'reddit':
            if (window._activeGloryCardData) HandOfGloryCard.shareToReddit(window._activeGloryCardData);
            break;
          case 'x':
            if (window._activeGloryCardData) HandOfGloryCard.shareToTwitter(window._activeGloryCardData);
            break;
          case 'facebook':
            if (window._activeGloryCardData) HandOfGloryCard.shareToFacebook(window._activeGloryCardData);
            break;
          case 'instagram':
            if (window._activeGloryCardData) HandOfGloryCard.shareToInstagram(window._activeGloryCardData);
            break;
          case 'threads':
            if (window._activeGloryCardData) HandOfGloryCard.shareToThreads(window._activeGloryCardData);
            break;
        }
      });
    }

    if (!window._gloryKeyBound && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window._gloryKeyBound = true;
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const el = document.getElementById('gloryOverlay');
          if (el && el.style.display !== 'none') {
            HandOfGloryCard.closeModal();
          }
        }
      });
    }

    if (box) {
      box.innerHTML = modalHtml;
    }

    // Fullscreen & 3D Room Container Reparenting & Stacking Context
    const fsElem = (typeof document !== 'undefined')
      ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
      : null;
    const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
      ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
      : null;
    const activeModalOverlay = document.getElementById('overlay');
    const modalParent = (activeModalOverlay && activeModalOverlay.parentElement && activeModalOverlay.parentElement !== document.body)
      ? activeModalOverlay.parentElement
      : null;
    const b3dContainer = document.getElementById('board3DContainer');
    const is3DFullscreen = Boolean(
      fsElem || b3dElem || (b3dContainer && (b3dContainer.classList?.contains?.('is-fullscreen') || (typeof document !== 'undefined' && document.body?.classList?.contains?.('is-3d-fullscreen'))))
    );

    const targetParent = fsElem || b3dElem || modalParent || (is3DFullscreen ? b3dContainer : null);
    if (targetParent && overlay.parentElement !== targetParent) {
      targetParent.appendChild(overlay);
    }

    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '20000000';
    overlay.style.pointerEvents = 'auto';
    overlay.style.display = 'flex';
  }

  /**
   * Closes the Hand of Glory modal without touching or disrupting the underlying game modal.
   */
  static closeModal() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('gloryOverlay');
    if (overlay) {
      overlay.style.display = 'none';
      const fsElem = (typeof document !== 'undefined')
        ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
        : null;
      const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
        : null;
      if (overlay.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
        document.body.appendChild(overlay);
      }
    }

    // Defensive check: ensure #btnNextHand is functional if present in the active show modal
    const nextBtn = document.getElementById('btnNextHand');
    if (nextBtn && !nextBtn.onclick) {
      nextBtn.onclick = () => {
        const state = (typeof window !== 'undefined' && window.state) || {};
        state.showModal = null;
        if (typeof window.hideModal === 'function') window.hideModal();
        if (typeof window.nextSeat === 'function') state.dealer = window.nextSeat(state.dealer);
        state.revealCrib = false;
        if (typeof window.newHand === 'function') window.newHand();
        if (typeof window.netPush === 'function') window.netPush({ modal: 'hide' });
      };
    }
  }
}

if (typeof window !== 'undefined') {
  window.downloadActiveGloryCard = () => {
    if (window._activeGloryCardData) HandOfGloryCard.downloadCardImage(window._activeGloryCardData);
  };
  window.copyActiveGloryCard = () => {
    if (window._activeGloryCardData) HandOfGloryCard.copyCardImage(window._activeGloryCardData);
  };
  window.shareActiveGloryNative = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareNative(window._activeGloryCardData);
  };
  window.shareActiveGloryReddit = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareToReddit(window._activeGloryCardData);
  };
  window.shareActiveGloryTwitter = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareToTwitter(window._activeGloryCardData);
  };
  window.shareActiveGloryFacebook = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareToFacebook(window._activeGloryCardData);
  };
  window.shareActiveGloryInstagram = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareToInstagram(window._activeGloryCardData);
  };
  window.shareActiveGloryThreads = () => {
    if (window._activeGloryCardData) HandOfGloryCard.shareToThreads(window._activeGloryCardData);
  };
  window.showHandOfGloryModal = (keyOrData, context) => {
    HandOfGloryCard.showModal(keyOrData, context);
  };
  window.closeHandOfGloryModal = () => {
    HandOfGloryCard.closeModal();
  };
}
